import { theme } from '../theme';

// Why: plain-text fields that are NOT email bodies (the attachment digest) must not go through the
// thread-cutting normalizer below — a digest that happens to mention "Von: … Gesendet: …" would be
// truncated by escapeHtmlText's thread-boundary logic. This is the pure escape step only.
// Why the full 5-char set (& < > " '): a near-miss on this branch (task-19/task-21) found a
// caller that builds an HTML attribute (href="...") from this output — `&`/`<`/`>` alone leave
// `"` and `'` free to break out of an attribute and inject markup. A helper whose safety depends
// on the caller already knowing it's attribute-unsafe is a footgun, so the escape set is widened
// here rather than left to each call site to compensate for. Single pass over one map — every
// match is replaced exactly once, so there is no re-escaping hazard to order around.
const escapeMap: Record<string, string> = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' };

export const escapeHtml = (s: string): string => s.replace(/[&<>"']/g, c => escapeMap[c]);

// Why: plain text (from StripHtmlToPlainText) set via dangerouslySetInnerHTML needs cleanup:
// 1. Strip Outlook safety banner (plain text remnant, can repeat in threaded emails)
// 2. Strip <mailto:>/<https:> URL remnants left by MimeKit in .eml TextBody
// 3. Truncate at first email thread boundary (CS rep reads thread in Outlook)
// 4. Strip mobile/client email footers ("Gesendet mit/von...", "Sent from...")
// 5. Collapse whitespace-only blank lines (Outlook HTML → spaces/nbsp between \n)
// 6. HTML-escape < > & so forwarding headers aren't parsed as HTML elements
// Must run BEFORE styleRedactionTokens so pill <span>s are the only HTML in the output.
export const escapeHtmlText = (s: string): string => {
  // Why: .msg BodyText from Outlook uses \r\n; \r breaks whitespace-collapse regex
  let t = s.replace(/\r\n?/g, '\n');
  // Why: Outlook safety banner includes trailing URL remnant on same line
  t = t.replace(/Sie erhalten nicht häufig E-Mails von[\s\S]*?(?:Erfahren Sie, warum dies wichtig ist|LearnAboutSenderIdentification|SafetyTips)[^\n]*\s*/g, '');
  t = t.replace(/<(?:mailto|https?):.*?>/g, '');
  // Why: only show first message — thread markers: inline forward, Outlook block, web.de format
  // Why: PII redaction replaces dates/names, so markers must match [REDACTED_*] tokens too
  const threadMarkers = [
    /\nAm (?:\d{2}\.\d{2}\.\d{2,4}|\[REDACTED_DATE\]) um \d{2}:\d{2} schrieb/,
    /\n\s*(?:Von:|From:)[\s\S]{0,200}?\n(?:Gesendet|Sent):/,
    /\n(?:Gesendet|Sent):\n[A-Z]/,
    /\n\[REDACTED_[A-Z]+\]\s*<?\[REDACTED_EMAIL\]>?\n(?:Gesendet|Sent):/,
  ];
  let cut = t.length;
  for (const re of threadMarkers) {
    const m = re.exec(t);
    if (m && m.index < cut) cut = m.index;
  }
  t = t.substring(0, cut);
  t = t.replace(/^(?:Gesendet (?:mit |von (?:meinem? |Outlook )).+|Sent from .+)\s*$/gm, '');
  t = t.replace(/\n([ \t\u00a0]*\n){2,}/g, '\n\n');
  t = t.trim();
  return escapeHtml(t);
};

export const isHtml = (text: string): boolean =>
  /<(html|div|p|br|span)\b/i.test(text);

// ─── linkifyUrls ────────────────────────────────────────
// Wraps http(s):// and www. URLs found in already-HTML-escaped text into anchor tags.
// Decodes &amp; back to & only inside the href attribute. Plain rendering uses the matched (escaped) form.
// Run AFTER escapeHtmlText, BEFORE styleRedactionTokens.
const URL_REGEX = /\b(https?:\/\/[^\s<>"]+|www\.[^\s<>"]+)/g;
export const linkifyUrls = (html: string): string =>
  html.replace(URL_REGEX, (match) => {
    let trimmed = match;
    let trailing = '';
    while (trimmed.length > 0 && /[.,;:!?)]/.test(trimmed.slice(-1))) {
      trailing = trimmed.slice(-1) + trailing;
      trimmed = trimmed.slice(0, -1);
    }
    // Why the trailing .replace(/"/g, '&quot;') is kept though now redundant (task-21): the
    // upstream contract (escapeHtml runs before linkifyUrls, per the module comment above) means
    // no raw " can reach this point anymore — escapeHtml already turned it into &quot;. This is
    // deliberate belt-and-braces for that upstream contract, not dead code: if a future caller
    // ever invokes linkifyUrls on non-pre-escaped text, this still stops attribute breakout on
    // its own. Do not remove without re-verifying the upstream guarantee still holds everywhere.
    const href = (trimmed.startsWith('www.') ? 'https://' + trimmed : trimmed)
      .replace(/&amp;/g, '&').replace(/"/g, '&quot;');
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:#000d66;text-decoration:underline">${trimmed}</a>${trailing}`;
  });

// ─── styleRedactionTokens ───────────────────────────────
// Replaces [REDACTED_*] tokens with styled pill spans.
// Must run AFTER DOMPurify.sanitize() — DOMPurify would strip injected spans.

// Maps the BE's [REDACTED_X] type slug → key in personal_data_json.
const TYPE_TO_PD_KEY: Record<string, string> = {
  EMAIL: 'emails', IBAN: 'ibans', PHONE: 'phoneNumbers',
  NAME: 'names', DATE: 'dates', PLZ: 'postalCodes',
};

// Reveal context for click-to-reveal (UploadsSeite pre-process only — see pii-reveal-defensibility.md).
// When `revealCtx` is undefined, behavior is identical to the original redaction-only render.
export interface RevealContext {
  personalData: Record<string, any>;     // keys: emails, ibans, phoneNumbers, names, dates, postalCodes
  revealed: Set<string>;                  // membership key: `${TYPE}:${ordinal}` (0-based)
}

export const styleRedactionTokens = (html: string, revealCtx?: RevealContext): string => {
  // Per-render ordinal counter so the Nth [REDACTED_NAME] maps to personal_data_json.names[N-1]
  const ordinals: Record<string, number> = {};
  return html.replace(
    /\[REDACTED_(EMAIL|IBAN|PHONE|NAME|DATE|PLZ)\]/g,
    (match, type: string) => {
      const idx = (ordinals[type] = (ordinals[type] ?? -1) + 1);
      const escaped = match.replace(/[<>&"']/g, c => escapeMap[c]);

      // Resolve the source value if a reveal context is provided.
      let revealedValue: string | null = null;
      if (revealCtx) {
        const pdKey = TYPE_TO_PD_KEY[type];
        const arr = pdKey ? revealCtx.personalData[pdKey] : undefined;
        if (Array.isArray(arr) && idx < arr.length && typeof arr[idx] === 'string') {
          revealedValue = arr[idx];
        }
      }
      const isRevealed = !!revealedValue && revealCtx?.revealed.has(`${type}:${idx}`);
      const cursor = revealCtx && revealedValue ? 'pointer' : 'default';
      const display = isRevealed
        ? revealedValue!.replace(/[<>&"']/g, c => escapeMap[c])
        : escaped;
      const bg = isRevealed ? '#fff7e6' : theme.colors.bgLight;       // soft amber when revealed → "you're seeing original"
      const border = isRevealed ? theme.colors.accentOrange : theme.colors.borderLight;
      const color = isRevealed ? '#7a3a00' : theme.colors.navyMid;

      const dataAttrs = revealCtx && revealedValue
        ? ` data-pii-type="${type}" data-pii-index="${idx}"`
        : '';
      return `<span${dataAttrs} style="background:${bg};border:1px solid ${border};color:${color};padding:1px 6px;border-radius:3px;font-size:11px;font-weight:500;white-space:nowrap;cursor:${cursor}">${display}</span>`;
    }
  );
};
